import { ComponentType, OverlayContainer } from '@angular/cdk/overlay';
import { ComponentRef, Injectable, Injector, inject } from '@angular/core';

import { AppConfig } from '@frontend/sports/common/client-config-data-access';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import {
    ActiveModal,
    IModalWindowStackService,
    ModalBackdropComponent,
    ModalGestureAnimationService,
    ModalOptions,
    ModalRef,
    ModalWindowStackService,
} from '@frontend/sports/modal/feature';

import { ModalWindowComponent } from './modal-window.component';

@Injectable({ providedIn: 'root' })
export class ModalStackService implements IModalWindowStackService {
    private timerService = inject(TimerService);
    private containerService = inject(OverlayContainer);
    private appConfig = inject(AppConfig);
    private modalWindowStackService = inject(ModalWindowStackService);
    private modalGestureAnimationService = inject(ModalGestureAnimationService);

    protected createModalRef<T>(...args: ConstructorParameters<typeof ModalRef<T>>): ModalRef<T> {
        return new ModalRef<T>(...args);
    }

    openModalWindow<T>(contentInjector: Injector, content: ComponentType<T>, options: ModalOptions): ModalRef<T> {
        const containerEl = options.container ?? this.containerService.getContainerElement();

        if (!containerEl) {
            throw new Error(`The specified modal container "${options.container?.tagName || 'body'}" was not found in the DOM.`);
        }

        if (options.centerVerticalToViewport) {
            options.windowClass = `${options.windowClass ?? ''} modal-vertical-align`;
        }

        const activeModal = new ActiveModal();
        const contentRef = this.modalWindowStackService.getContentRef(options.injector || contentInjector, content, activeModal);

        const coverCmptRef = options.gestureOptions?.enabled ? this.modalWindowStackService.attachCoverElement(containerEl) : undefined;
        const backdropCmptRef = options.backdrop !== false ? this.modalWindowStackService.attachBackdrop(containerEl) : undefined;

        const windowCmptRef = this.modalWindowStackService.attachWindowComponent(containerEl, contentRef, ModalWindowComponent);

        const modalRef = this.createModalRef(windowCmptRef, contentRef, backdropCmptRef, coverCmptRef, options.beforeDismiss);
        activeModal.result = modalRef.result;

        let modalInstance = windowCmptRef.instance as ModalWindowComponent;

        activeModal.close = (result?: any) => {
            if (!options.closeAnimation) {
                modalRef.close(result);
                return;
            }
            if (
                options.closeAnimation &&
                backdropCmptRef?.instance?.class?.includes('close-animation') &&
                !this.appConfig.modalDialogGesturesEnabled
            ) {
                modalRef.addBackdropClass('hide-backdrop');
            }
            if (this.appConfig.modalDialogGesturesEnabled && modalInstance.gestureOptions?.enabled) {
                this.modalGestureAnimationService.slideOutToBottom({ modalWindow: modalRef.windowRef }).finally(() => {
                    modalRef.close(result);
                });
            } else {
                modalInstance.windowClass += ` ${options.closeAnimation}`;
            }
            this.timerService.setTimeout(() => {
                modalRef.close(result);
            }, 350);
        };
        activeModal.dismiss = (reason: unknown) => {
            modalRef.dismiss(reason);
        };

        this.applyOptions(modalInstance, options, containerEl, windowCmptRef, backdropCmptRef);
        return modalRef;
    }

    applyOptions(
        modalInstance: ModalWindowComponent,
        options: ModalOptions,
        containerEl: HTMLElement,
        windowCmptRef: ComponentRef<ModalWindowComponent>,
        backdropCmptRef: ComponentRef<ModalBackdropComponent> | undefined,
    ): void {
        this.modalWindowStackService.applyWindowOptions(modalInstance, options);
        if (options.container && options.container === containerEl && options.centerVerticalToViewport) {
            this.modalWindowStackService.applyVerticalCentering(windowCmptRef, containerEl);
        }
        if (backdropCmptRef?.instance) {
            this.modalWindowStackService.applyBackdropOptions(backdropCmptRef.instance, options);
        }
    }
}
